import { useMemo } from "react";
import { createSubscribableArray, SubscribableArray, useSubscribableArray } from "../utils/subscribable-array";
import { joinMiddlewares } from "./join";
import { MessageMiddleware, MessageMiddlewareBuilder } from "./message-middleware";
import { UnknownMessage } from "../components/UknownMessage";

// registration would like below
// (window["xappMsgMiddlewares"] = window["xappMsgMiddlewares"] || []).push(next => (msg, ctx) => next(msg, ctx));

const storage = createStorage<MessageMiddlewareBuilder>(globalThis, "xappMsgMiddlewares");

const empty = [];

interface StorageType<T> {
    get(): readonly T[] | SubscribableArray<T> | undefined;
    set(vals: SubscribableArray<T>): void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function createStorage<T>(global: any, key: string): StorageType<T> {
    return {
        get: () => global[key],
        set: val => global[key] = val
    };
}

export function useGlobalSubscribableArray<T>(storage: StorageType<T>): readonly T[] {
    const raw = storage.get() || (empty as readonly T[]);

    const array = Array.isArray(raw) ? createSubscribableArray<T>(raw) : raw as SubscribableArray<T>;
    
    if (array !== raw && array.getArray.length > 0) {
        storage.set(array);
    }
    
    return useSubscribableArray(array);
}

export function useLateMiddlewaresBuilder(storage: StorageType<MessageMiddlewareBuilder>): MessageMiddlewareBuilder {
    const middlewares = useGlobalSubscribableArray(storage);

    return useMemo<MessageMiddlewareBuilder>(() => joinMiddlewares(middlewares), [middlewares]);
}

export function useMiddleware(): MessageMiddleware {
    const builder = useLateMiddlewaresBuilder(storage);

    return useMemo<MessageMiddleware>(() => builder(UnknownMessage), [builder]);
}
